import { ContextProvider } from '@src/providers';
import {
  DeviceTypeProvider,
  DROPDOWN_PORTAL_ID,
  MODAL_PORTAL_ID,
  POPOVER_PORTAL_ID,
  PORTAL_PARENT_CLASS,
  PortalContextProvider,
  useAuthContext,
  usePortal,
} from '@talos/kyoko';
import { useEffect } from 'react';
import { Route, Routes } from 'react-router-dom';
import { POPOUT_URL } from '../../components/Layout/tokens';
import { useMobileLayoutState } from '../../hooks/useMobileLayoutState';
import { useMobileLayoutViewport } from '../../hooks/useMobileLayoutViewport';
import { MobileLayoutProvider } from '../../providers/MobileLayoutContext';
import MobilePages from '../Mobile/MobilePages';
import { Unauthorized } from '../Unauthorized';
import { EmbeddedPages } from './EmbeddedPages';
import { Pages } from './Pages';

// ContextProvider must stay above the MobilePages/Pages fork here, or toggling isMobileLayout would remount the Redux store and drop unsaved layout state.
function AuthenticatedApp({ isMobileLayout }: { isMobileLayout: boolean }) {
  return (
    <ContextProvider>
      {isMobileLayout ? (
        <MobilePages />
      ) : (
        <PortalContextProvider>
          <Pages />
        </PortalContextProvider>
      )}
    </ContextProvider>
  );
}

export function DeviceRouter() {
  const { isMobileLayout, isDesktopLayoutForcedForSession } = useMobileLayoutState();
  useMobileLayoutViewport(isDesktopLayoutForcedForSession);
  const auth = useAuthContext();

  const { setPortalRef: modalPortalRef } = usePortal(MODAL_PORTAL_ID);
  const { setPortalRef: dropdownPortalRef } = usePortal(DROPDOWN_PORTAL_ID);
  const { setPortalRef: popoverPortalRef } = usePortal(POPOVER_PORTAL_ID);

  return (
    <DeviceTypeProvider>
      <MobileLayoutProvider isMobileLayout={isMobileLayout}>
        {/* Portal targets for rendering our components at the top level */}
        <div
          ref={modalPortalRef}
          id={MODAL_PORTAL_ID}
          data-testid={MODAL_PORTAL_ID}
          className={`ag-custom-component-popup ${PORTAL_PARENT_CLASS}`}
        />
        <div
          ref={dropdownPortalRef}
          id={DROPDOWN_PORTAL_ID}
          data-testid={DROPDOWN_PORTAL_ID}
          className={`ag-custom-component-popup ${PORTAL_PARENT_CLASS}`}
        />
        <div
          ref={popoverPortalRef}
          id={POPOVER_PORTAL_ID}
          data-testid={POPOVER_PORTAL_ID}
          className={`ag-custom-component-popup ${PORTAL_PARENT_CLASS}`}
        />
        <Routes>
          {auth.isAuthenticated && !isMobileLayout && (
            <>
              <Route path={POPOUT_URL} element={<SelfDestructIfEmpty />} />
              <Route path="/embedded/*" element={<EmbeddedPages />} />
            </>
          )}
          {auth.isAuthenticated && <Route path="*" element={<AuthenticatedApp isMobileLayout={isMobileLayout} />} />}
          {!auth.isAuthenticated && <Route path="/unauthorized/*" element={<Unauthorized />} />}
        </Routes>
      </MobileLayoutProvider>
    </DeviceTypeProvider>
  );
}

const DOCKVIEW_POPOUT_CONTENT_SELECTOR = '.dv-popout-window > .dv-groupview > .dv-content-container > .dv-react-part';
// This component is used to close the popout window if it is empty
// This is a workaround for the issue where duplicate popout windows are created
const SelfDestructIfEmpty = () => {
  useEffect(() => {
    setTimeout(() => {
      // Selector for the content container of the popout window
      const contentContainer = document
        .getElementById('dv-popout-window')
        ?.querySelector(DOCKVIEW_POPOUT_CONTENT_SELECTOR);
      // Check if the content container is empty
      // If it is, close the popout window
      if (contentContainer?.childElementCount === 0) {
        window.close();
      }
    }, 100);
  }, []);

  return <></>;
};
